import { v4 as uuidv4 } from "uuid";
import { photoshop } from "../globals";
import { useListenFromRenderer } from "../message";
import { postToRenderer } from "../message/utils";
import { checkPromptSafety } from "./shared/check-prompt-safety";
import { fetchInferenceJob } from "./shared/fetch-inference-job";
import { getTargetEditorImage } from "./shared/target-editor-image";

const callGptEditing = async (
	prompt: string,
	resolution: string,
	model: string,
	quality: "low" | "medium" | "high",
	input_images: string[],
	messageId: string,
	jwt: string,
	tool_document_id: string,
	batch_size: number,
) => {
	const promptSafetyResult = checkPromptSafety(prompt);
	if (promptSafetyResult.detected) {
		await promptSafetyResult.onFailed();
		return false;
	}

	const { success } = await getTargetEditorImage({
		commandName: "GPT - Image Editing",
		resultLayerName: "GPT - Image Editing",
		includeMask: true,
		applyMaskToResultLayer: true,
		placeAsSmartObject: true,
		maxSide: undefined,
		processImage: async ({ executionContext, imageBase64 }) => {
			const data = {
				prompt,
				model,
				input_images: [imageBase64, ...input_images],
				from_editor: true,
				resolution,
				quality,
				jwt,
				tool_document_id,
				batch_size,
			};

			return fetchGptEditing(data, executionContext, messageId);
		},
	});

	if (success) {
		const doc = photoshop.app.activeDocument;
		if (doc) {
			postToRenderer([
				{
					key: "documentInfoChanged",
					id: uuidv4(),
					values: {
						width: doc.width,
						height: doc.height,
					},
				},
			]);
		}
	}

	return success;
};

export async function fetchGptEditing(
	params: {
		prompt: string;
		model: string;
		input_images: string[];
		from_editor: boolean;
		resolution: string;
		quality: "low" | "medium" | "high";
		jwt: string;
		tool_document_id: string;
		batch_size: number;
	},
	executionContext: any,
	messageId: string,
): Promise<string | string[] | false> {
	return fetchInferenceJob({
		params,
		executionContext,
		messageId,
		apiPath: "/infer/gpt",
		statusEndpoint: (apiUrl, jobId) =>
			`${apiUrl.replace("/infer/gpt", "")}/status/${jobId}`,
		resultEndpoint: (apiUrl, jobId) =>
			`${apiUrl.replace("/infer/gpt", "")}/result/${jobId}`,
		cancelEndpoint: (apiUrl, jobId) => ({
			url: `${apiUrl.replace("/infer/gpt", "")}/cancel/${jobId}`,
			method: "POST",
		}),
		extractResult: (data) => data.image_base64_list ?? [data.image_base64],
		useGatewayUrl: true,
	});
}

export function useListenGptEditingMessage() {
	useListenFromRenderer([
		{
			key: "gptEditing",
			onMessage: async (message) => {
				const success = await callGptEditing(
					message.values.prompt,
					message.values.size,
					message.values.model,
					message.values.quality,
					message.values.input_images,
					message.id,
					message.values.jwt,
					message.values.tool_document_id,
					message.values.batch_size,
				);
				postToRenderer([
					{
						key: "finish",
						id: uuidv4(),
						values: {
							id: message.id,
							status: success ? "success" : "error",
						},
					},
				]);
			},
		},
	]);
}
